import { contactRelevance } from './scoring.js';
import { routeRoleWithOpportunity } from './role-routing.js';
import { normalizeCompanyName } from './normalise.js';
import { opportunityOutreachPlan } from './opportunity-outreach.js';
import { outreachCooldownUntil, sameCooldownOffice } from './cooldown.js';
import { activeCompanyTeamStatus, requiresCustomTeamReengagement } from './company-team-status.js';
import { isOpportunitySnoozed, opportunityTimingStage } from './opportunity-timing.js';
/** Read-only candidate shortlist. Send gates continue to be enforced by Prepare and the worker. */
export function bestOpportunityContacts(opportunity, contacts, companies, sequences, now = Date.now(), limit = 5) {
    const named = new Set([opportunity.companyName, ...(opportunity.targetCompanies || []).map(target => target.name)].map(name => normalizeCompanyName(name || '')).filter(Boolean));
    if (!named.size)
        return [];
    const sent = sequences.filter(item => !item.archived && item.stage === 'sent');
    const pending = sequences.filter(item => !item.archived && item.stage !== 'sent');
    const timeBlocked = isOpportunitySnoozed(opportunity, now) || ['too-late', 'too-early'].includes(opportunityTimingStage(opportunity, now));
    return contacts.filter(contact => !contact.archived && named.has(normalizeCompanyName(contact.companyName)))
        .map(contact => {
        const company = companies.find(item => !item.archived && (item.id === contact.companyId || normalizeCompanyName(item.name) === normalizeCompanyName(contact.companyName)));
        const plan = opportunityOutreachPlan(opportunity, contact.companyName, contacts, sequences, companies);
        const routed = routeRoleWithOpportunity(contact.title, opportunity);
        const category = contact.crmCategory === 'Misc' && routed.crmCategory !== 'Misc' ? routed.crmCategory : contact.crmCategory;
        const fit = contactRelevance(contact.title);
        const titleMatch = (opportunity.recommendedTitles || []).some(title => title.toLowerCase().split(/[^a-z]+/).filter(word => word.length >= 5).some(word => contact.title.toLowerCase().includes(word)));
        const score = Math.min(100, fit + (category === plan.category ? 8 : 0) + (titleMatch ? 5 : 0));
        const ownSent = sent.some(item => item.contactId === contact.id) || !!contact.contactedAt;
        const ownPending = pending.some(item => item.contactId === contact.id);
        const relatedCooldown = sent.some(item => item.contactId !== contact.id && item.crmCategory === category && !!item.firstSentAt && contacts.some(other => other.id === item.contactId && normalizeCompanyName(other.companyName) === normalizeCompanyName(contact.companyName) && sameCooldownOffice(other.locationLabel, contact.locationLabel) && outreachCooldownUntil(item.firstSentAt, opportunity).getTime() > now));
        const suppressed = !!company?.doNotContact || !!activeCompanyTeamStatus(company, category, now) || !!requiresCustomTeamReengagement(company, category, now);
        const restricted = !!contact.doNotContact || !!contact.deadLeadReason || suppressed || contact.emailVerification === 'bounced';
        const email = String(contact.email || '').trim();
        const verified = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(email) && ['verified', 'pattern_confirmed'].includes(contact.emailVerification);
        let status = 'ready';
        let reason = 'Relevant decision-maker · confirmed email';
        if (restricted) {
            status = 'restricted';
            reason = 'Do not contact, team suppression, re-entry gate or bounced address';
        }
        else if (ownSent) {
            status = 'contacted';
            reason = 'Already contacted';
        }
        else if (ownPending) {
            status = 'planned';
            reason = 'Already queued or scheduled';
        }
        else if (relatedCooldown) {
            status = 'cooldown';
            reason = 'Same-team / same-office cooldown applies';
        }
        else if (timeBlocked) {
            status = 'research';
            reason = 'Opportunity timing or snooze needs review';
        }
        else if (fit < 60) {
            status = 'research';
            reason = 'Role is not a strong commercial match';
        }
        else if (!verified) {
            status = 'verify';
            reason = 'Email requires verification or resolution in Prepare';
        }
        const canPrepare = status === 'ready' || status === 'verify';
        return { contact, status, reason, score, canPrepare, category };
    })
        .sort((a, b) => Number(b.canPrepare) - Number(a.canPrepare) || b.score - a.score || a.contact.fullName.localeCompare(b.contact.fullName))
        .slice(0, Math.max(1, limit));
}
